import { useId } from "react";

import { Button, cx } from "@/components/ui";

import { PayIcon, type PayMethod } from "./PayIcon";

export type { PayMethod } from "./PayIcon";

export const PAY_METHOD_LABELS: Record<PayMethod, string> = {
  card: "Card",
  apple_pay: "Apple Pay",
  google_pay: "Google Pay",
  bacs_debit: "Bank debit",
};

const ALL: PayMethod[] = ["card", "apple_pay", "google_pay", "bacs_debit"];

/** Figma "Payment method selector" (Billing — Add payment method, node 4107:7397). `methods`: the
 * ones offered here (all by default). `compact`: in a narrow window (the domain checkout's), the
 * buttons share one row evenly with tighter padding so their labels fit. */
export function PaymentMethodSelector({
  value,
  onChange,
  methods = ALL,
  compact = false,
}: {
  value: PayMethod;
  onChange: (next: PayMethod) => void;
  methods?: PayMethod[];
  compact?: boolean;
}) {
  const labelId = useId();
  const wallets = methods
    .filter((m) => m === "apple_pay" || m === "google_pay")
    .map((m) => PAY_METHOD_LABELS[m]);
  return (
    <div className="flex flex-col gap-2.5">
      <p id={labelId} className="text-xs leading-4 font-medium text-fg-muted">
        How would you like to pay
      </p>
      <div
        role="group"
        aria-labelledby={labelId}
        className={compact ? "grid gap-2" : "grid grid-cols-2 gap-2.5 sm:flex"}
        style={compact ? { gridTemplateColumns: `repeat(${methods.length}, minmax(0, 1fr))` } : undefined}
      >
        {methods.map((m) => {
          const on = m === value;
          return (
            <Button
              unstyled
              key={m}
              type="button"
              aria-pressed={on}
              onClick={() => onChange(m)}
              className={cx(
                "flex min-w-0 items-center justify-center rounded-[10px] py-3 leading-5 font-semibold whitespace-nowrap",
                compact ? "gap-1.5 px-2 text-[13px]" : "gap-2 px-3.5 text-sm sm:flex-1",
                on
                  ? "border-[1.5px] border-brand-500 bg-[#fef8f6] text-[#b23117]"
                  : "border border-[#d4cac4] bg-surface text-[#443e3b] hover:bg-muted",
              )}
            >
              <PayIcon method={m} className={on ? "text-[#b23117]" : "text-fg-muted"} />
              {PAY_METHOD_LABELS[m]}
            </Button>
          );
        })}
      </div>
      <p className="text-xs leading-4 text-fg-subtle">
        {wallets.length > 0 &&
          `${wallets.join(" and ")} ${wallets.length > 1 ? "appear" : "appears"} on devices that support ${wallets.length > 1 ? "them" : "it"}.`}
        {methods.includes("bacs_debit") &&
          " Bank debit is for UK bank accounts and takes a few working days to clear, so it pays renewals and upgrades, not purchases made now."}
      </p>
    </div>
  );
}
